import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Confirm Delete",
  message,
  itemName,
  itemType = "item",
  isDangerous = false,
  isLoading = false,
  additionalWarning,
  confirmText = "Delete",
  cancelText = "Cancel"
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (isDangerous && confirmationText?.toLowerCase() !== 'delete') {
      return;
    }
    onConfirm();
  };

  const handleClose = () => {
    if (!isLoading) {
      setConfirmationText('');
      setShowDetails(false);
      onClose();
    }
  };

  const defaultMessage = `Are you sure you want to delete ${itemName ? `"${itemName}"` : `this ${itemType}`}? This action cannot be undone.`;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDangerous ? 'bg-error/10' : 'bg-warning/10'
            }`}>
              <Icon 
                name={isDangerous ? "AlertTriangle" : "Trash2"} 
                size={20} 
                className={isDangerous ? 'text-error' : 'text-warning'} 
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
              {itemName && (
                <p className="text-sm text-muted-foreground capitalize">{itemType}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <p className="text-muted-foreground">
              {message || defaultMessage}
            </p>

            {/* Item Details */}
            {itemName && (
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="Info" size={16} className="text-info flex-shrink-0" />
                  <span className="text-sm text-foreground font-medium">{itemName}</span>
                </div>
              </div>
            )}

            {/* Additional Warning */}
            {additionalWarning && (
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-warning font-medium">Warning</p>
                    <p className="text-sm text-warning/80 mt-1">{additionalWarning}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Input for Dangerous Actions */}
            {isDangerous && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  Type "DELETE" to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e?.target?.value)}
                  placeholder="DELETE"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-error focus:border-error"
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Impact Details */}
            {showDetails && (
              <div className="bg-muted/20 border border-border rounded-lg p-3">
                <h4 className="text-sm font-medium text-foreground mb-2">What will happen:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• The {itemType} will be permanently removed</li>
                  <li>• All associated data will be deleted</li>
                  <li>• This action cannot be reversed</li>
                  {itemType === 'vendor' && (
                    <li>• Related expenses and bills will be preserved but marked as "Deleted Vendor"</li>
                  )}
                  {itemType === 'item' && (
                    <li>• Any pending orders or invoices will need to be updated manually</li>
                  )}
                </ul>
              </div>
            )}

            {/* Toggle Details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-primary hover:underline flex items-center space-x-1"
              type="button"
              disabled={isLoading}
            >
              <span>{showDetails ? 'Hide' : 'Show'} details</span>
              <Icon 
                name={showDetails ? 'ChevronUp' : 'ChevronDown'} 
                size={14} 
              />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isLoading || (isDangerous && confirmationText?.toLowerCase() !== 'delete')}
              loading={isLoading}
              iconName={isLoading ? "Loader2" : "Trash2"}
              iconPosition="left"
            >
              {isLoading ? 'Deleting...' : confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;